import { Injectable, inject } from '@angular/core';
import { DfHeaderUi } from '../df-header-ui.interfaces';
import { Meta } from '@df/core/meta/meta';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { BehaviorSubject } from 'rxjs';
import { IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { Model } from '@df/catalog/model';
import { ICmsBlock } from '@icms/core/resource/block/icms-block';
import { App } from '@df/core/app';
import { Catalog } from '@df/catalog/catalog.interfaces';

@Injectable({
    providedIn: 'root'
})
export class DfHeaderUiService {
    /**
     * Module config
     */
    private moduleConfig = inject(DfHeaderUi.OPTIONS);

    /**
     * APp service
     */
    private app = inject(App);

    /**
     * Meta service
     */
    private meta = inject(Meta);

    /**
     * Block resource
     */
    private iCmsBlockResource = inject(ICmsBlockResource);
    /**
     * Data change service
     */
    private iCmsDataChangeService = inject(ICmsDataChangeService);

    /**
     * If header is transparent
     */
    readonly isTransparent$ = new BehaviorSubject<boolean>(false);

    /**
     * Theme id
     */
    readonly theme$ = new BehaviorSubject<string>('');

    constructor() {
        this.meta.subscribe(model => this.update());

        this.iCmsDataChangeService.afterChange$.subscribe(event => {
            if (event.changeType === NICmsDataChange.EChangeType.change) {
                // page property changes
                if ((event.model as Model) === this.meta.model) {
                    // transparency
                    if (this.iCmsDataChangeService.filterByData(event, ICmsPage.type, [['data', 'additional', 'dfHeaderUiTransparent']])) {
                        return this.updateTransparency();
                    }
                    // theme
                    if (this.iCmsDataChangeService.filterByData(event, ICmsPage.type, [['data', 'additional', 'dfHeaderUiTheme']])) {
                        return this.updateTheme();
                    }
                }
                // block property changes
                if (event.model.type === ICmsBlock.type && (event.model as ICmsBlock).data?.url === this.moduleConfig.headerComponentId) {
                    // transparency
                    if (
                        this.iCmsDataChangeService.filterByData(event, ICmsBlock.type, [
                            ['data', 'content', 'styles', 'dfHeaderUiTransparent']
                        ])
                    ) {
                        return this.updateTransparency();
                    }
                    // theme
                    if (
                        this.iCmsDataChangeService.filterByData(event, ICmsBlock.type, [['data', 'content', 'styles', 'dfHeaderUiTheme']])
                    ) {
                        return this.updateTheme();
                    }
                }
            }
        });

        this.update();
    }

    /**
     * Update all properties
     * NOTE: this is prepared for future extension with hide up or light mode
     */
    private update() {
        this.updateTransparency();
        this.updateTheme();
    }

    /**
     * Update transparency state
     */
    private updateTransparency(): void {
        const isTransparent = () => {
            const field = this.moduleConfig.fields.transparency;
            if (!field) {
                return false;
            }
            const state = field.check ? field.check(this.app) : undefined;
            if (state !== undefined) {
                return state;
            }
            const page = this.meta.model;
            if (page && page.type === ICmsPage.type) {
                const state = (page.data as IPageDataV3)?.additional?.dfHeaderUiTransparent;
                if (state === DfHeaderUi.Field.Page.EOverrideOption.on) {
                    return true;
                }
                if (state === DfHeaderUi.Field.Page.EOverrideOption.off) {
                    return false;
                }
            }

            const styles = this.getHeaderBlockStyles();
            if (styles?.dfHeaderUiTransparent) {
                return true;
            }
            return false;
        };

        const value = isTransparent();
        if (value !== this.isTransparent$.value) {
            this.isTransparent$.next(value);
        }
    }

    /**
     * Update transparency state
     */
    private updateTheme(): void {
        const getTheme = (): string => {
            const field = this.moduleConfig.fields.theme;
            if (!field) {
                return '';
            }
            const state = field.check ? field.check(this.app) : undefined;
            if (state !== undefined) {
                return state;
            }
            const page = this.meta.model;
            if (page && page.type === ICmsPage.type) {
                const state = (page.data as IPageDataV3)?.additional?.dfHeaderUiTheme;
                if (state) {
                    return state;
                }
            }
            return this.getHeaderBlockStyles()?.dfHeaderUiTheme || '';
        };

        const value = getTheme();
        if (value !== this.theme$.value) {
            this.theme$.next(value);
        }
    }

    private getHeaderBlockStyles(): Catalog.IStyles | undefined {
        const block = this.iCmsBlockResource.getAllExisting().find(i => i.data.url === this.moduleConfig.headerComponentId);
        return block.data?.content?.styles || undefined;
    }
}
